import React, { useState, useEffect } from 'react';
import { adminApi } from '../../api/admin';
import { marketplaceApi } from '../../api/marketplace';
import { ShoppingBag, Gavel, DollarSign, AlertTriangle, TrendingUp, Package, Users, Filter, Search, Plus, X, Check, Clock, Edit, Trash2, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Info, History } from 'lucide-react';
import CompanyAuctionCard from './marketplace/CompanyAuctionCard';
import CompanySaleCard from './marketplace/CompanySaleCard';
import CompanyLoanCard from './marketplace/CompanyLoanCard';
import AdminAuctionCard from './marketplace/AdminAuctionCard';
import AdminSaleCard from './marketplace/AdminSaleCard';
import AdminLoanCard from './marketplace/AdminLoanCard';
import { CardImage } from '../../utils/cardImageUtils';
import { useLanguage } from '../../i18n/LanguageContext';

const AdminMarketplace = () => {
  const { locale } = useLanguage();
  const [activeTab, setActiveTab] = useState('overview'); // overview, company-cards, auctions, sales, loans, concluded-auctions, concluded-sales, alerts
  const [stats, setStats] = useState(null);
  const [activity, setActivity] = useState([]);
  const [companyCards, setCompanyCards] = useState([]);
  const [allCompanyCards, setAllCompanyCards] = useState([]);
  const [companyAuctions, setCompanyAuctions] = useState([]);
  const [companySales, setCompanySales] = useState([]);
  const [companyLoans, setCompanyLoans] = useState([]);
  const [allAuctions, setAllAuctions] = useState([]);
  const [allSales, setAllSales] = useState([]);
  const [allLoans, setAllLoans] = useState([]);
  const [concludedAuctions, setConcludedAuctions] = useState([]);
  const [totalConcludedAuctions, setTotalConcludedAuctions] = useState(0);
  const [concludedSearch, setConcludedSearch] = useState('');
  const [debouncedConcludedSearch, setDebouncedConcludedSearch] = useState('');
  const [concludedSales, setConcludedSales] = useState([]);
  const [totalConcludedSales, setTotalConcludedSales] = useState(0);
  const [concludedSalesSearch, setConcludedSalesSearch] = useState('');
  const [debouncedConcludedSalesSearch, setDebouncedConcludedSalesSearch] = useState('');
  const [pageCompanyCards, setPageCompanyCards] = useState(0);
  const [pageAllAuctions, setPageAllAuctions] = useState(0);
  const [pageAllSales, setPageAllSales] = useState(0);
  const [pageAllLoans, setPageAllLoans] = useState(0);
  const [pageConcludedAuctions, setPageConcludedAuctions] = useState(0);
  const [pageConcludedSales, setPageConcludedSales] = useState(0);
  const [loading, setLoading] = useState(false);
  const [companyCardsLoading, setCompanyCardsLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [filters, setFilters] = useState({
    search: '',
    rarity: '',
    position: '',
    nationality: '',
    league: '',
    team: ''
  });
  const [debouncedFilters, setDebouncedFilters] = useState({
    search: '',
    rarity: '',
    position: '',
    nationality: '',
    league: '',
    team: ''
  });
  const [companyCardsFilterOptions, setCompanyCardsFilterOptions] = useState({ leagues: [], teams: [], teamsByLeague: {} });
  const [showSaleModal, setShowSaleModal] = useState(false);
  const [showAuctionModal, setShowAuctionModal] = useState(false);
  const [showLoanModal, setShowLoanModal] = useState(false);
  const [selectedCard, setSelectedCard] = useState(null);
  const [salePrice, setSalePrice] = useState('');
  const [saleQuantity, setSaleQuantity] = useState(1);
  const [auctionStartingPrice, setAuctionStartingPrice] = useState('');
  const [auctionDurationHours, setAuctionDurationHours] = useState(24);
  const [auctionDurationMinutes, setAuctionDurationMinutes] = useState(0);
  const [auctionDurationSeconds, setAuctionDurationSeconds] = useState(0);
  const [auctionQuantity, setAuctionQuantity] = useState(1);
  const [auctionPriceMode, setAuctionPriceMode] = useState('fixed');
  const [loanPricePerGW, setLoanPricePerGW] = useState('');
  const [loanDurationGW, setLoanDurationGW] = useState(1);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [infoCard, setInfoCard] = useState(null);
  const [loanQuantity, setLoanQuantity] = useState(1);
  const [deleteCardQuantity, setDeleteCardQuantity] = useState(1);
  const [deletingCompanyCards, setDeletingCompanyCards] = useState(false);
  const [auctionDefaultPrices, setAuctionDefaultPrices] = useState(null); // { basic: 1, plus: 1, ... } from API
  
  // Modals for editing
  const [showEditSaleModal, setShowEditSaleModal] = useState(false);
  const [showEditAuctionModal, setShowEditAuctionModal] = useState(false);
  const [showEditLoanModal, setShowEditLoanModal] = useState(false);
  const [editingSale, setEditingSale] = useState(null);
  const [editingAuction, setEditingAuction] = useState(null);
  const [editingLoan, setEditingLoan] = useState(null);
  const [newSalePrice, setNewSalePrice] = useState('');
  const [newAuctionEndTime, setNewAuctionEndTime] = useState('');
  const [newLoanPricePerGW, setNewLoanPricePerGW] = useState('');
  const [newLoanDurationGW, setNewLoanDurationGW] = useState(1);
  
  // Pagination for overview listings
  const [pageAuctions, setPageAuctions] = useState(0);
  const [pageSales, setPageSales] = useState(0);
  const [pageLoans, setPageLoans] = useState(0);
  const ITEMS_PER_PAGE = 6;
  const COMPANY_CARDS_PER_PAGE = 8;
  
  // Alerts state
  const [abuseAlerts, setAbuseAlerts] = useState([]);
  const [alertFilters, setAlertFilters] = useState({
    status: '',
    severity: '',
    alertType: ''
  });
  
  // Prevent double clicks on cancel auction
  const [cancellingAuctionId, setCancellingAuctionId] = useState(null);

  // Team batch auction
  const [selectedTeamsForBatch, setSelectedTeamsForBatch] = useState([]);
  const [teamAuctionLeague, setTeamAuctionLeague] = useState('');
  const [teamAuctionTeamToAdd, setTeamAuctionTeamToAdd] = useState('');
  const [teamAuctionRarities, setTeamAuctionRarities] = useState([]);
  const [teamAuctionStartDate, setTeamAuctionStartDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    return d.toISOString().slice(0, 10);
  });
  const [teamAuctionStartTime, setTeamAuctionStartTime] = useState('01:00');
  const [showTeamAuctionPreviewModal, setShowTeamAuctionPreviewModal] = useState(false);
  const [teamAuctionPreviewData, setTeamAuctionPreviewData] = useState(null);
  const [teamAuctionPreviewLoading, setTeamAuctionPreviewLoading] = useState(false);
  const [teamAuctionConfirming, setTeamAuctionConfirming] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedFilters(filters);
    }, 350);
    return () => clearTimeout(timer);
  }, [filters]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedConcludedSearch(concludedSearch);
      if (activeTab === 'concluded-auctions') setPageConcludedAuctions(0);
    }, 350);
    return () => clearTimeout(timer);
  }, [concludedSearch]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedConcludedSalesSearch(concludedSalesSearch);
      if (activeTab === 'concluded-sales') setPageConcludedSales(0);
    }, 350);
    return () => clearTimeout(timer);
  }, [concludedSalesSearch]);

  useEffect(() => {
    loadData();
  }, [activeTab, debouncedFilters, debouncedConcludedSearch, debouncedConcludedSalesSearch, pageCompanyCards, pageAllAuctions, pageAllSales, pageAllLoans, pageConcludedAuctions, pageConcludedSales]);

  useEffect(() => {
    adminApi.getAuctionDefaultPrices()
      .then((res) => {
        const p = res?.data?.prices;
        if (p) {
          setAuctionDefaultPrices({
            basic: p.auctionDefaultPriceBasic,
            plus: p.auctionDefaultPricePlus,
            rare: p.auctionDefaultPriceRare,
            epic: p.auctionDefaultPriceEpic,
            unique: p.auctionDefaultPriceUnique,
            legend: p.auctionDefaultPriceLegend,
            divinity: p.auctionDefaultPriceDivinity
          });
        }
      })
      .catch(() => {});
  }, []);

  // Load league/team options when opening Company Cards tab
  useEffect(() => {
    if (activeTab === 'company-cards') {
      adminApi.getCompanyCardsFilterOptions()
        .then((res) => {
          const data = res?.data ?? res;
          if (data?.success && (data.leagues?.length > 0 || data.teams?.length > 0 || data.teamsByLeague)) {
            setCompanyCardsFilterOptions({
              leagues: data.leagues || [],
              teams: data.teams || [],
              teamsByLeague: data.teamsByLeague || {}
            });
          }
        })
        .catch(() => {});
    }
  }, [activeTab]);
  
  // Reset pagination when switching tabs
  useEffect(() => {
    if (activeTab === 'overview') {
      setPageAuctions(0);
      setPageSales(0);
      setPageLoans(0);
    } else if (activeTab === 'company-cards') {
      setPageCompanyCards(0);
    } else if (activeTab === 'auctions') {
      setPageAllAuctions(0);
    } else if (activeTab === 'concluded-auctions') {
      setPageConcludedAuctions(0);
    } else if (activeTab === 'concluded-sales') {
      setPageConcludedSales(0);
    } else if (activeTab === 'sales') {
      setPageAllSales(0);
    } else if (activeTab === 'loans') {
      setPageAllLoans(0);
    }
  }, [activeTab]);
  
  // Helper functions to get paginated items (sempre array per evitare .map/.slice is not a function)
  const getPaginatedItems = (items, page) => {
    const list = Array.isArray(items) ? items : [];
    const start = page * ITEMS_PER_PAGE;
    const end = start + ITEMS_PER_PAGE;
    return list.slice(start, end);
  };

  const getTotalPages = (items) => {
    const list = Array.isArray(items) ? items : [];
    return Math.ceil(list.length / ITEMS_PER_PAGE);
  };

  const normalizeRarity = (rarity) => {
    if (!rarity) return 'basic';
    return String(rarity).toLowerCase();
  };

  const getCardDisplayName = (card) => {
    return card?.player?.display_name || card?.player?.fullName || card?.player?.full_name || card?.customName || 'Card';
  };

  const getCardDisplayType = (card) => {
    const kind = String(card?.cardKind || '').toLowerCase();
    if (kind === 'legend') return 'Legend';
    if (kind === 'divinity') return 'Divinity';
    return 'Player';
  };

  const getDefaultAuctionPrice = (rarity) => {
    const rarityKey = normalizeRarity(rarity);
    if (auctionDefaultPrices && typeof auctionDefaultPrices[rarityKey] === 'number') {
      return auctionDefaultPrices[rarityKey];
    }
    const fallback = {
      basic: 1,
      plus: 1,
      rare: 2,
      epic: 20,
      unique: 100,
      divinity: 100,
      legend: 100
    };
    return fallback[rarityKey] ?? 1;
  };

  const loadData = async () => {
    if (activeTab === 'company-cards') {
      setCompanyCardsLoading(true);
    } else {
      setLoading(true);
    }
    try {
      if (activeTab === 'overview') {
        const statsResponse = await adminApi.getMarketplaceStats();
        if (statsResponse?.success) {
          setStats(statsResponse.stats);
        }

        const activityResponse = await adminApi.getMarketplaceActivity(7);
        if (activityResponse?.success) {
          setActivity(activityResponse.activity || []);
        }

        // Load company active listings from marketplace API (filtered for seller_id = null)
        const [auctionsResponse, salesResponse, loansResponse] = await Promise.all([
          marketplaceApi.getAuctions({}),
          marketplaceApi.getSales({}),
          marketplaceApi.getLoans({})
        ]);
        
        // Filter for company listings (seller_id or lender_id = null)
        if (auctionsResponse?.success) {
          const rawAuctions = auctionsResponse.auctions;
          const allAuctions = Array.isArray(rawAuctions) ? rawAuctions : [];
          const companyOnly = allAuctions.filter(a => a.seller_id === null || a.seller_id === undefined);
          console.log('[DEBUG] Company Auctions:', {
            total: allAuctions.length,
            company: companyOnly.length,
            sample: allAuctions[0] ? { id: allAuctions[0].id, seller_id: allAuctions[0].seller_id } : null
          });
          setCompanyAuctions(companyOnly);
        } else {
          console.log('[DEBUG] Auctions response error:', auctionsResponse);
          setCompanyAuctions([]);
        }
        if (salesResponse?.success) {
          const rawSales = salesResponse.sales;
          const allSales = Array.isArray(rawSales) ? rawSales : [];
          const companyOnly = allSales.filter(s => s.seller_id === null || s.seller_id === undefined);
          console.log('[DEBUG] Company Sales:', {
            total: allSales.length,
            company: companyOnly.length,
            sample: allSales[0] ? { id: allSales[0].id, seller_id: allSales[0].seller_id } : null
          });
          setCompanySales(companyOnly);
        } else {
          console.log('[DEBUG] Sales response error:', salesResponse);
          setCompanySales([]);
        }
        if (loansResponse?.success) {
          const rawLoans = loansResponse.loans;
          const allLoans = Array.isArray(rawLoans) ? rawLoans : [];
          const companyOnly = allLoans.filter(l => l.lender_id === null || l.lender_id === undefined);
          console.log('[DEBUG] Company Loans:', {
            total: allLoans.length,
            company: companyOnly.length,
            sample: allLoans[0] ? { id: allLoans[0].id, lender_id: allLoans[0].lender_id } : null
          });
          setCompanyLoans(companyOnly);
        } else {
          console.log('[DEBUG] Loans response error:', loansResponse);
          setCompanyLoans([]);
        }
      } else if (activeTab === 'company-cards') {
        const cardsResponse = await adminApi.getCompanyCards(debouncedFilters);
        if (cardsResponse?.success) {
          console.log('[DEBUG] Company cards response:', {
            filters: debouncedFilters,
            cardsCount: cardsResponse.cards?.length || 0,
            cards: cardsResponse.cards
          });
          const allCards = Array.isArray(cardsResponse.cards) ? cardsResponse.cards : [];
          setAllCompanyCards(allCards);
          const start = pageCompanyCards * COMPANY_CARDS_PER_PAGE;
          setCompanyCards(allCards.slice(start, start + COMPANY_CARDS_PER_PAGE));
        } else {
          setCompanyCards([]);
          setAllCompanyCards([]);
        }
      } else if (activeTab === 'auctions') {
        const auctionsResponse = await marketplaceApi.getAuctions({});
        if (auctionsResponse?.success) {
          const allAuctionsData = Array.isArray(auctionsResponse.auctions) ? auctionsResponse.auctions : [];
          setAllAuctions(allAuctionsData);
        } else {
          setAllAuctions([]);
        }
      } else if (activeTab === 'concluded-auctions') {
        const res = await adminApi.getConcludedAuctions({
          limit: ITEMS_PER_PAGE,
          offset: pageConcludedAuctions * ITEMS_PER_PAGE,
          search: debouncedConcludedSearch.trim() || undefined
        });
        if (res?.success) {
          setConcludedAuctions(Array.isArray(res.auctions) ? res.auctions : []);
          setTotalConcludedAuctions(res.total ?? 0);
        } else {
          setConcludedAuctions([]);
          setTotalConcludedAuctions(0);
        }
      } else if (activeTab === 'concluded-sales') {
        const res = await adminApi.getConcludedSales({
          limit: ITEMS_PER_PAGE,
          offset: pageConcludedSales * ITEMS_PER_PAGE,
          search: debouncedConcludedSalesSearch.trim() || undefined
        });
        if (res?.success) {
          setConcludedSales(Array.isArray(res.sales) ? res.sales : []);
          setTotalConcludedSales(res.total ?? 0);
        } else {
          setConcludedSales([]);
          setTotalConcludedSales(0);
        }
      } else if (activeTab === 'sales') {
        const salesResponse = await marketplaceApi.getSales({});
        if (salesResponse?.success) {
          const allSalesData = Array.isArray(salesResponse.sales) ? salesResponse.sales : [];
          setAllSales(allSalesData);
        } else {
          setAllSales([]);
        }
      } else if (activeTab === 'loans') {
        const loansResponse = await marketplaceApi.getLoans({});
        if (loansResponse?.success) {
          const allLoansData = Array.isArray(loansResponse.loans) ? loansResponse.loans : [];
          setAllLoans(allLoansData);
        } else {
          setAllLoans([]);
        }
      } else if (activeTab === 'alerts') {
        const alertsResponse = await adminApi.getAbuseAlerts(alertFilters);
        if (alertsResponse?.success) {
          setAbuseAlerts(Array.isArray(alertsResponse.alerts) ? alertsResponse.alerts : []);
        } else {
          setAbuseAlerts([]);
        }
      }
    } catch (error) {
      console.error('Error loading marketplace data:', error);
      setMessage({ type: 'error', text: `❌ ${error.message || 'Failed to load data'}` });
    } finally {
      setLoading(false);
      setCompanyCardsLoading(false);
    }
  };

  const handleCreateCompanySale = async () => {
    if (!salePrice || parseFloat(salePrice) <= 0) {
      setMessage({ type: 'error', text: 'Please enter a valid price' });
      return;
    }

    if (!saleQuantity || parseInt(saleQuantity) <= 0 || parseInt(saleQuantity) > selectedCard.availableCopies) {
      setMessage({ type: 'error', text: `Please enter a valid quantity (max ${selectedCard.availableCopies})` });
      return;
    }

    setLoading(true);
    try {
      await adminApi.createCompanySale({
        templateId: selectedCard.templateId,
        price: parseFloat(salePrice),
        quantity: parseInt(saleQuantity)
      });
      setMessage({ type: 'success', text: `✅ ${saleQuantity} card(s) listed for sale successfully!` });
      setShowSaleModal(false);
      setSalePrice('');
      setSaleQuantity(1);
      setSelectedCard(null);
      loadData();
    } catch (error) {
      setMessage({ type: 'error', text: `❌ ${error.message || 'Failed to create sale'}` });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCompanyAuction = async () => {
    if (!auctionStartingPrice || parseFloat(auctionStartingPrice) <= 0) {
      setMessage({ type: 'error', text: 'Please enter a valid starting price' });
      return;
    }

    if (!auctionQuantity || parseInt(auctionQuantity) <= 0 || parseInt(auctionQuantity) > selectedCard.availableCopies) {
      setMessage({ type: 'error', text: `Please enter a valid quantity (max ${selectedCard.availableCopies})` });
      return;
    }

    const totalSeconds = (parseInt(auctionDurationHours, 10) * 3600)
      + (parseInt(auctionDurationMinutes, 10) * 60)
      + parseInt(auctionDurationSeconds, 10);
    if (!Number.isFinite(totalSeconds) || totalSeconds <= 0) {
      setMessage({ type: 'error', text: 'Duration must be greater than 0 seconds' });
      return;
    }

    const templateId = selectedCard?.templateId != null ? Number(selectedCard.templateId) : null;
    if (templateId == null || !Number.isFinite(templateId)) {
      setMessage({ type: 'error', text: 'Invalid card selection. Please select a single card.' });
      return;
    }
    setLoading(true);
    try {
      await adminApi.createCompanyAuction({
        templateId,
        startingPrice: parseFloat(auctionStartingPrice),
        durationHours: auctionDurationHours,
        durationMinutes: auctionDurationMinutes,
        durationSeconds: auctionDurationSeconds,
        quantity: parseInt(auctionQuantity, 10) || 1
      });
      setMessage({ type: 'success', text: `✅ ${auctionQuantity} auction(s) created successfully!` });
      setShowAuctionModal(false);
      setAuctionStartingPrice('');
      setAuctionDurationHours(24);
      setAuctionDurationMinutes(0);
      setAuctionDurationSeconds(0);
      setAuctionQuantity(1);
      setSelectedCard(null);
      loadData();
    } catch (error) {
      setMessage({ type: 'error', text: `❌ ${error.message || 'Failed to create auction'}` });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCompanyLoan = async () => {
    if (!loanPricePerGW || parseFloat(loanPricePerGW) <= 0) {
      setMessage({ type: 'error', text: 'Please enter a valid price per gameweek' });
      return;
    }

    if (![1, 3, 5, 10].includes(loanDurationGW)) {
      setMessage({ type: 'error', text: 'Duration must be 1, 3, 5, or 10 gameweeks' });
      return;
    }

    if (!loanQuantity || parseInt(loanQuantity) <= 0 || parseInt(loanQuantity) > selectedCard.availableCopies) {
      setMessage({ type: 'error', text: `Please enter a valid quantity (max ${selectedCard.availableCopies})` });
      return;
    }

    setLoading(true);
    try {
      await adminApi.createCompanyLoan({
        templateId: selectedCard.templateId,
        pricePerGW: parseFloat(loanPricePerGW),
        durationGW: loanDurationGW,
        quantity: parseInt(loanQuantity)
      });
      setMessage({ type: 'success', text: `✅ ${loanQuantity} card(s) listed for loan successfully!` });
      setShowLoanModal(false);
      setLoanPricePerGW('');
      setLoanDurationGW(1);
      setLoanQuantity(1);
      setSelectedCard(null);
      loadData();
    } catch (error) {
      setMessage({ type: 'error', text: `❌ ${error.message || 'Failed to create loan'}` });
    } finally {
      setLoading(false);
    }
  };

  const handleTeamAuctionPreview = async () => {
    if (!selectedTeamsForBatch?.length) {
      setMessage({ type: 'error', text: 'Seleziona almeno un team' });
      return;
    }
    const [h, m] = (teamAuctionStartTime || '01:00').split(':').map(Number);
    const startDt = new Date(teamAuctionStartDate + 'T' + String(h).padStart(2, '0') + ':' + String(m).padStart(2, '0') + ':00');
    if (startDt <= new Date()) {
      setMessage({ type: 'error', text: 'Data e ora di inizio devono essere successive a ora' });
      return;
    }
    setTeamAuctionPreviewLoading(true);
    setTeamAuctionPreviewData(null);
    try {
      const res = await adminApi.previewTeamAuctions(selectedTeamsForBatch, teamAuctionStartDate, teamAuctionRarities?.length ? teamAuctionRarities : undefined, teamAuctionStartTime || undefined);
      const data = res?.data ?? res;
      if (data?.success && Array.isArray(data?.slots)) {
        setTeamAuctionPreviewData(data);
        setShowTeamAuctionPreviewModal(true);
      } else {
        setMessage({ type: 'error', text: data?.error || 'Nessuna card disponibile per i team selezionati' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: err?.response?.data?.error || err?.message || 'Preview fallita' });
    } finally {
      setTeamAuctionPreviewLoading(false);
    }
  };

  const handleTeamAuctionConfirm = async () => {
    if (!selectedTeamsForBatch?.length || !teamAuctionStartDate) return;
    setTeamAuctionConfirming(true);
    try {
      const res = await adminApi.createTeamBatchAuctions(selectedTeamsForBatch, teamAuctionStartDate, teamAuctionRarities?.length ? teamAuctionRarities : undefined, teamAuctionStartTime || undefined);
      const data = res?.data ?? res;
      const created = data?.created ?? 0;
      const failed = data?.failed ?? 0;
      if (created > 0) {
        setMessage({ type: 'success', text: `✅ ${created} asta/e create con successo${failed > 0 ? `. ${failed} fallite.` : ''}` });
        setShowTeamAuctionPreviewModal(false);
        setTeamAuctionPreviewData(null);
        setSelectedTeamsForBatch([]);
        loadData();
      }
      if (failed > 0 && created === 0) {
        setMessage({ type: 'error', text: `Tutte le aste sono fallite. ${(data?.errors || []).join(' ')}` });
      }
      if (data?.errors?.length) {
        console.warn('Team batch auction errors:', data.errors);
      }
    } catch (err) {
      setMessage({ type: 'error', text: err?.response?.data?.error || err?.message || 'Creazione fallita' });
    } finally {
      setTeamAuctionConfirming(false);
    }
  };

  // Edit Sale Price
  const handleEditSalePrice = (sale) => {
    setEditingSale(sale);
    setNewSalePrice(sale.price);
    setShowEditSaleModal(true);
  };

  const handleUpdateSalePrice = async () => {
    if (!editingSale || !newSalePrice || parseFloat(newSalePrice) <= 0) {
      setMessage({ type: 'error', text: '❌ Please enter a valid price' });
      return;
    }
    setLoading(true);
    try {
      await adminApi.updateSalePrice(editingSale.id, parseFloat(newSalePrice));
      setMessage({ type: 'success', text: '✅ Sale price updated successfully!' });
      setShowEditSaleModal(false);
      setEditingSale(null);
      setNewSalePrice('');
      loadData();
    } catch (error) {
      setMessage({ type: 'error', text: `❌ ${error.message || 'Failed to update sale price'}` });
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveSale = async (saleId) => {
    if (!window.confirm('Are you sure you want to remove this sale?')) {
      return;
    }
    setLoading(true);
    try {
      await adminApi.removeSale(saleId);
      setMessage({ type: 'success', text: '✅ Sale removed successfully!' });
      loadData();
    } catch (error) {
      setMessage({ type: 'error', text: `❌ ${error.message || 'Failed to remove sale'}` });
    } finally {
      setLoading(false);
    }
  };

  // Edit Auction End Time
  const handleEditAuctionEndTime = (auction) => {
    setEditingAuction(auction);
    const endTime = new Date(auction.end_time);
    const localDateTime = new Date(endTime.getTime() - endTime.getTimezoneOffset() * 60000)
      .toISOString()
      .slice(0, 16);
    setNewAuctionEndTime(localDateTime);
    setShowEditAuctionModal(true);
  };

  const handleUpdateAuctionEndTime = async () => {
    if (!editingAuction || !newAuctionEndTime) {
      setMessage({ type: 'error', text: '❌ Please enter a valid end time' });
      return;
    }
    setLoading(true);
    try {
      await adminApi.updateAuctionEndTime(editingAuction.id, newAuctionEndTime);
      setMessage({ type: 'success', text: '✅ Auction end time updated successfully!' });
      setShowEditAuctionModal(false);
      setEditingAuction(null);
      setNewAuctionEndTime('');
      loadData();
    } catch (error) {
      setMessage({ type: 'error', text: `❌ ${error.message || 'Failed to update auction end time'}` });
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveAuction = async (auctionId) => {
    if (!window.confirm('Are you sure you want to remove this auction? Current bidders will be refunded.')) {
      return;
    }
    setLoading(true);
    try {
      await adminApi.removeAuction(auctionId);
      setMessage({ type: 'success', text: '✅ Auction removed successfully!' });
      loadData();
    } catch (error) {
      setMessage({ type: 'error', text: `❌ ${error.message || 'Failed to remove auction'}` });
    } finally {
      setLoading(false);
    }
  };

  // Cancel Auction (with refund option)
  const handleCancelAuction = async (auctionId) => {
    // Prevent double clicks
    if (cancellingAuctionId === auctionId || loading) {
      return;
    }
    
    const reason = window.prompt('Enter reason for cancellation:');
    if (!reason || !reason.trim()) {
      return;
    }
    const refundBidders = window.confirm('Refund current bidders?');
    
    setCancellingAuctionId(auctionId);
    setLoading(true);
    try {
      await adminApi.cancelAuction(auctionId, { reason: reason.trim(), refundBidders });
      setMessage({ type: 'success', text: '✅ Auction cancelled successfully!' });
      loadData();
    } catch (error) {
      setMessage({ type: 'error', text: `❌ ${error.message || 'Failed to cancel auction'}` });
    } finally {
      setLoading(false);
      setCancellingAuctionId(null);
    }
  };

  // Block Card
  const handleBlockCard = async (cardId) => {
    const reason = window.prompt('Enter reason for blocking:');
    if (!reason || !reason.trim()) {
      return;
    }
    const blockType = window.prompt('Block type (marketplace/tournament/all):', 'all');
    if (!['marketplace', 'tournament', 'all'].includes(blockType?.toLowerCase())) {
      setMessage({ type: 'error', text: '❌ Invalid block type. Use: marketplace, tournament, or all' });
      return;
    }
    setLoading(true);
    try {
      await adminApi.blockCard(cardId, { reason: reason.trim(), blockType: blockType.toLowerCase() });
      setMessage({ type: 'success', text: '✅ Card blocked successfully!' });
      loadData();
    } catch (error) {
      setMessage({ type: 'error', text: `❌ ${error.message || 'Failed to block card'}` });
    } finally {
      setLoading(false);
    }
  };

  // Process Refund
  const handleProcessRefund = async (userId, amount, reason, relatedType, relatedId) => {
    if (!userId || !amount || !reason) {
      setMessage({ type: 'error', text: '❌ User ID, amount, and reason are required' });
      return;
    }
    setLoading(true);
    try {
      await adminApi.processRefund({ userId, amount, reason, relatedType, relatedId });
      setMessage({ type: 'success', text: `✅ Refund of ${amount} DIOS processed successfully!` });
      loadData();
    } catch (error) {
      setMessage({ type: 'error', text: `❌ ${error.message || 'Failed to process refund'}` });
    } finally {
      setLoading(false);
    }
  };

  // Edit Loan Details
  const handleEditLoanDetails = (loan) => {
    setEditingLoan(loan);
    setNewLoanPricePerGW(loan.price_per_gw);
    setNewLoanDurationGW(loan.duration_gw);
    setShowEditLoanModal(true);
  };

  const handleUpdateLoanDetails = async () => {
    if (!editingLoan || !newLoanPricePerGW || parseFloat(newLoanPricePerGW) <= 0) {
      setMessage({ type: 'error', text: '❌ Please enter valid loan details' });
      return;
    }
    setLoading(true);
    try {
      await adminApi.updateLoanDetails(editingLoan.id, {
        pricePerGW: parseFloat(newLoanPricePerGW),
        durationGW: newLoanDurationGW
      });
      setMessage({ type: 'success', text: '✅ Loan details updated successfully!' });
      setShowEditLoanModal(false);
      setEditingLoan(null);
      setNewLoanPricePerGW('');
      setNewLoanDurationGW(1);
      loadData();
    } catch (error) {
      setMessage({ type: 'error', text: `❌ ${error.message || 'Failed to update loan details'}` });
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveLoan = async (loanId) => {
    if (!window.confirm('Are you sure you want to remove this loan?')) {
      return;
    }
    setLoading(true);
    try {
      await adminApi.removeLoan(loanId);
      setMessage({ type: 'success', text: '✅ Loan removed successfully!' });
      loadData();
    } catch (error) {
      setMessage({ type: 'error', text: `❌ ${error.message || 'Failed to remove loan'}` });
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'overview', name: 'Overview', icon: TrendingUp },
    { id: 'company-cards', name: 'Company Cards', icon: Package },
    { id: 'auctions', name: 'Auctions', icon: Gavel },
    { id: 'concluded-auctions', name: 'Storico aste', icon: History },
    { id: 'concluded-sales', name: 'Storico sales', icon: DollarSign },
    { id: 'sales', name: 'Sales attive', icon: DollarSign },
    { id: 'loans', name: 'Loans', icon: ShoppingBag },
    { id: 'alerts', name: 'Alerts', icon: AlertTriangle }
  ];

  return (
    <div className="min-h-screen bg-gray-900 text-white p-8">
      <div className="max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-3xl font-bold flex items-center">
          <ShoppingBag className="w-8 h-8 mr-3 text-blue-400" />
          Marketplace Management
        </h1>
      </div>

        {/* Tabs */}
        <div className="flex space-x-2 mb-6 overflow-x-auto">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center space-x-2 px-6 py-3 rounded-lg font-semibold whitespace-nowrap transition-all ${
                  activeTab === tab.id
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                }`}
              >
                <Icon className="w-5 h-5" />
                <span>{tab.name}</span>
              </button>
            );
          })}
        </div>

        {/* Message Display */}
        {message.text && (
          <div className={`mb-6 p-4 rounded-lg ${
            message.type === 'success' 
              ? 'bg-green-900/50 border border-green-500 text-green-300' 
              : 'bg-red-900/50 border border-red-500 text-red-300'
          }`}>
            {message.text}
            <button
              type="button"
              onClick={() => setMessage({ type: '', text: '' })}
              className="modal-close-btn ml-4"
              aria-label="Chiudi"
            >
              ×
            </button>
          </div>
        )}

        {/* Content */}
        {loading && activeTab !== 'company-cards' && (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-400"></div>
            <p className="mt-4 text-gray-400">Loading...</p>
          </div>
        )}

        {!loading && activeTab === 'overview' && stats && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-gray-400 text-sm">Active Auctions</span>
                  <Gavel className="w-5 h-5 text-blue-400" />
                </div>
                <div className="text-3xl font-bold text-blue-400">{stats.activeAuctions}</div>
                <div className="text-xs text-gray-500 mt-1">
                  Total: {stats.totalAuctions}
                  {stats.companyActiveAuctions > 0 && (
                    <span className="text-blue-400 ml-2">• Company: {stats.companyActiveAuctions}</span>
                  )}
                </div>
              </div>

              <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-gray-400 text-sm">Active Sales</span>
                  <DollarSign className="w-5 h-5 text-green-400" />
                </div>
                <div className="text-3xl font-bold text-green-400">{stats.activeSales}</div>
                <div className="text-xs text-gray-500 mt-1">
                  Total: {stats.totalSales}
                  {stats.companyActiveSales > 0 && (
                    <span className="text-green-400 ml-2">• Company: {stats.companyActiveSales}</span>
                  )}
                </div>
              </div>

              <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-gray-400 text-sm">Marketplace Volume</span>
                  <TrendingUp className="w-5 h-5 text-yellow-400" />
                </div>
                <div className="text-3xl font-bold text-yellow-400">{stats.marketplaceVolume.toFixed(2)}</div>
                <div className="text-xs text-gray-500 mt-1">DIOS (30 days)</div>
              </div>

              <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-gray-400 text-sm">Open Alerts</span>
                  <AlertTriangle className="w-5 h-5 text-red-400" />
                </div>
                <div className="text-3xl font-bold text-red-400">{stats.openAlerts}</div>
                {stats.criticalAlerts > 0 && (
                  <div className="text-xs text-red-400 mt-1">{stats.criticalAlerts} Critical</div>
                )}
              </div>
            </div>

            {/* Company Active Listings - Marketplace View */}
            {(companyAuctions.length > 0 || companySales.length > 0 || companyLoans.length > 0) && (
              <div className="mt-8">
                <h2 className="text-2xl font-bold mb-6">Company Active Listings</h2>
                
                {/* Active Auctions */}
                {companyAuctions.length > 0 && (
                  <div className="mb-8">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-xl font-semibold text-purple-400 flex items-center">
                        <Gavel className="w-6 h-6 mr-2" />
                        Active Auctions ({companyAuctions.length})
                      </h3>
                      {getTotalPages(companyAuctions) > 1 && (
                        <div className="flex items-center space-x-2 text-sm text-gray-400">
                          <span>
                            Showing {pageAuctions * ITEMS_PER_PAGE + 1}-{Math.min((pageAuctions + 1) * ITEMS_PER_PAGE, companyAuctions.length)} of {companyAuctions.length}
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {(Array.isArray(companyAuctions) ? getPaginatedItems(companyAuctions, pageAuctions) : []).map((auction) => (
                        <CompanyAuctionCard 
                          key={auction.id} 
                          auction={auction}
                          onEditEndTime={handleEditAuctionEndTime}
                          onRemove={handleRemoveAuction}
                          onCancel={handleCancelAuction}
                          cancellingAuctionId={cancellingAuctionId}
                        />
                      ))}
                    </div>
                    {getTotalPages(companyAuctions) > 1 && (
                      <div className="flex items-center justify-center space-x-4 mt-6">
                        <button
                          onClick={() => setPageAuctions(Math.max(0, pageAuctions - 1))}
                          disabled={pageAuctions === 0}
                          className={`px-4 py-2 rounded-lg font-semibold transition-all flex items-center ${
                            pageAuctions === 0
                              ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                              : 'bg-gray-700 text-white hover:bg-gray-600'
                          }`}
                        >
                          <ChevronLeft className="w-5 h-5 mr-1" />
                          Previous
                        </button>
                        <span className="text-white/90 text-sm font-medium">
                          Page {pageAuctions + 1} of {getTotalPages(companyAuctions)}
                        </span>
                        <button
                          onClick={() => setPageAuctions(Math.min(getTotalPages(companyAuctions) - 1, pageAuctions + 1))}
                          disabled={pageAuctions >= getTotalPages(companyAuctions) - 1}
                          className={`px-4 py-2 rounded-lg font-semibold transition-all flex items-center ${
                            pageAuctions >= getTotalPages(companyAuctions) - 1
                              ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                              : 'bg-gray-700 text-white hover:bg-gray-600'
                          }`}
                        >
                          Next
                          <ChevronRight className="w-5 h-5 ml-1" />
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {/* Active Sales */}
                {companySales.length > 0 && (
                  <div className="mb-8">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-xl font-semibold text-green-400 flex items-center">
                        <DollarSign className="w-6 h-6 mr-2" />
                        Active Sales ({companySales.length})
                      </h3>
                      {getTotalPages(companySales) > 1 && (
                        <div className="flex items-center space-x-2 text-sm text-gray-400">
                          <span>
                            Showing {pageSales * ITEMS_PER_PAGE + 1}-{Math.min((pageSales + 1) * ITEMS_PER_PAGE, companySales.length)} of {companySales.length}
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {(Array.isArray(companySales) ? getPaginatedItems(companySales, pageSales) : []).map((sale) => (
                        <CompanySaleCard 
                          key={sale.id} 
                          sale={sale}
                          onEditPrice={handleEditSalePrice}
                          onRemove={handleRemoveSale}
                        />
                      ))}
                    </div>
                    {getTotalPages(companySales) > 1 && (
                      <div className="flex items-center justify-center space-x-4 mt-6">
                        <button
                          onClick={() => setPageSales(Math.max(0, pageSales - 1))}
                          disabled={pageSales === 0}
                          className={`px-4 py-2 rounded-lg font-semibold transition-all flex items-center ${
                            pageSales === 0
                              ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                              : 'bg-gray-700 text-white hover:bg-gray-600'
                          }`}
                        >
                          <ChevronLeft className="w-5 h-5 mr-1" />
                          Previous
                        </button>
                        <span className="text-white/90 text-sm font-medium">
                          Page {pageSales + 1} of {getTotalPages(companySales)}
                        </span>
                        <button
                          onClick={() => setPageSales(Math.min(getTotalPages(companySales) - 1, pageSales + 1))}
                          disabled={pageSales >= getTotalPages(companySales) - 1}
                          className={`px-4 py-2 rounded-lg font-semibold transition-all flex items-center ${
                            pageSales >= getTotalPages(companySales) - 1
                              ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                              : 'bg-gray-700 text-white hover:bg-gray-600'
                          }`}
                        >
                          Next
                          <ChevronRight className="w-5 h-5 ml-1" />
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {/* Active Loans */}
                {companyLoans.length > 0 && (
                  <div className="mb-8">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-xl font-semibold text-yellow-400 flex items-center">
                        <Clock className="w-6 h-6 mr-2" />
                        Active Loans ({companyLoans.length})
                      </h3>
                      {getTotalPages(companyLoans) > 1 && (
                        <div className="flex items-center space-x-2 text-sm text-gray-400">
                          <span>
                            Showing {pageLoans * ITEMS_PER_PAGE + 1}-{Math.min((pageLoans + 1) * ITEMS_PER_PAGE, companyLoans.length)} of {companyLoans.length}
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {(Array.isArray(companyLoans) ? getPaginatedItems(companyLoans, pageLoans) : []).map((loan) => (
                        <CompanyLoanCard 
                          key={loan.id} 
                          loan={loan}
                          onEditDetails={handleEditLoanDetails}
                          onRemove={handleRemoveLoan}
                        />
                      ))}
                    </div>
                    {getTotalPages(companyLoans) > 1 && (
                      <div className="flex items-center justify-center space-x-4 mt-6">
                        <button
                          onClick={() => setPageLoans(Math.max(0, pageLoans - 1))}
                          disabled={pageLoans === 0}
                          className={`px-4 py-2 rounded-lg font-semibold transition-all flex items-center ${
                            pageLoans === 0
                              ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                              : 'bg-gray-700 text-white hover:bg-gray-600'
                          }`}
                        >
                          <ChevronLeft className="w-5 h-5 mr-1" />
                          Previous
                        </button>
                        <span className="text-white/90 text-sm font-medium">
                          Page {pageLoans + 1} of {getTotalPages(companyLoans)}
                        </span>
                        <button
                          onClick={() => setPageLoans(Math.min(getTotalPages(companyLoans) - 1, pageLoans + 1))}
                          disabled={pageLoans >= getTotalPages(companyLoans) - 1}
                          className={`px-4 py-2 rounded-lg font-semibold transition-all flex items-center ${
                            pageLoans >= getTotalPages(companyLoans) - 1
                              ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                              : 'bg-gray-700 text-white hover:bg-gray-600'
                          }`}
                        >
                          Next
                          <ChevronRight className="w-5 h-5 ml-1" />
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {activeTab === 'company-cards' && (
          <>
            {/* Filters */}
            <div className="bg-gray-800 rounded-lg p-6 mb-6 border border-gray-700">
              <div className="flex items-center space-x-2 mb-4">
                <Filter className="w-5 h-5 text-blue-400" />
                <h2 className="text-xl font-bold">Filters</h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7 gap-4">
                <div className="md:col-span-2 xl:col-span-2">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search player name..."
                      value={filters.search}
                      onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                      className="w-full pl-10 pr-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
                    />
                  </div>
                </div>
                <select
                  value={filters.rarity}
                  onChange={(e) => setFilters({ ...filters, rarity: e.target.value })}
                  className="px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
                >
                  <option value="">All Rarities</option>
                  <option value="basic">Basic</option>
                  <option value="plus">Plus</option>
                  <option value="rare">Rare</option>
                  <option value="epic">Epic</option>
                  <option value="unique">Unique</option>
                </select>
                <select
                  value={filters.position}
                  onChange={(e) => setFilters({ ...filters, position: e.target.value })}
                  className="px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
                >
                  <option value="">All Positions</option>
                  <option value="GK">Goalkeeper</option>
                  <option value="DEF">Defender</option>
                  <option value="MID">Midfielder</option>
                  <option value="ATT">Attacker</option>
                </select>
                <input
                  type="text"
                  placeholder="Nationality"
                  value={filters.nationality}
                  onChange={(e) => setFilters({ ...filters, nationality: e.target.value })}
                  className="px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
                />
                <select
                  value={filters.league}
                  onChange={(e) => setFilters({ ...filters, league: e.target.value })}
                  className="px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
                >
                  <option value="">All Leagues</option>
                  {Array.isArray(companyCardsFilterOptions.leagues) && companyCardsFilterOptions.leagues.map((league) => (
                    <option key={league} value={league}>{league}</option>
                  ))}
                </select>
                <select
                  value={filters.team}
                  onChange={(e) => setFilters({ ...filters, team: e.target.value })}
                  className="px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
                >
                  <option value="">All Teams</option>
                  {Array.isArray(companyCardsFilterOptions.teams) && companyCardsFilterOptions.teams.map((team) => (
                    <option key={team} value={team}>{team}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Team Batch Auction */}
            <div className="bg-gray-800 rounded-lg p-6 mb-6 border border-gray-700 border-dashed border-purple-500/50">
              <div className="flex items-center space-x-2 mb-4">
                <Gavel className="w-5 h-5 text-purple-400" />
                <h2 className="text-xl font-bold">Asta intero team</h2>
              </div>
              <p className="text-gray-400 text-sm mb-4">
                Metti in asta tutte le card disponibili dei team selezionati. Scadenze: 00:00-08:00 ogni ora (1:00, 2:00…8:00), 08:00-23:59 ogni 15 min.
              </p>
              <div className="space-y-4">
                <div className="flex flex-wrap items-end gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">Lega</label>
                    <select
                      value={teamAuctionLeague}
                      onChange={(e) => {
                        setTeamAuctionLeague(e.target.value);
                        setTeamAuctionTeamToAdd('');
                      }}
                      className="px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-purple-500 min-w-[180px]"
                    >
                      <option value="">Seleziona lega</option>
                      {Array.isArray(companyCardsFilterOptions.leagues) && companyCardsFilterOptions.leagues.map((l) => (
                        <option key={l} value={l}>{l}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">Team</label>
                    <select
                      value={teamAuctionTeamToAdd}
                      onChange={(e) => {
                        const team = e.target.value;
                        if (team && !selectedTeamsForBatch.includes(team)) {
                          setSelectedTeamsForBatch([...selectedTeamsForBatch, team]);
                          setTeamAuctionTeamToAdd('');
                        }
                      }}
                      className="px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-purple-500 min-w-[180px]"
                      disabled={!teamAuctionLeague}
                    >
                      <option value="">Seleziona team</option>
                      {teamAuctionLeague && Array.isArray(companyCardsFilterOptions.teamsByLeague?.[teamAuctionLeague]) &&
                        companyCardsFilterOptions.teamsByLeague[teamAuctionLeague].map((t) => (
                          <option key={t} value={t}>{t}</option>
                        ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">Rarità (più selezioni)</label>
                    <div className="flex flex-wrap gap-3 px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg">
                      {['basic', 'plus', 'rare', 'epic', 'unique'].map((r) => (
                        <label key={r} className="flex items-center gap-2 cursor-pointer text-sm">
                          <input
                            type="checkbox"
                            checked={teamAuctionRarities.includes(r)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setTeamAuctionRarities([...teamAuctionRarities, r]);
                              } else {
                                setTeamAuctionRarities(teamAuctionRarities.filter((x) => x !== r));
                              }
                            }}
                            className="rounded border-gray-500 text-purple-600 focus:ring-purple-500"
                          />
                          <span className="capitalize">{r}</span>
                        </label>
                      ))}
                    </div>
                    <span className="text-xs text-gray-500">Vuoto = tutte le rarità</span>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">Data inizio</label>
                    <input
                      type="date"
                      value={teamAuctionStartDate}
                      min={new Date().toISOString().slice(0, 10)}
                      onChange={(e) => setTeamAuctionStartDate(e.target.value)}
                      className="px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-purple-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">Ora inizio</label>
                    <input
                      type="time"
                      value={teamAuctionStartTime}
                      onChange={(e) => setTeamAuctionStartTime(e.target.value)}
                      className="px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-purple-500"
                    />
                    <span className="text-xs text-gray-500 block">Prima asta scade a quest'ora (data+ora &gt; ora)</span>
                  </div>
                  <button
                    type="button"
                    onClick={handleTeamAuctionPreview}
                    disabled={teamAuctionPreviewLoading || !selectedTeamsForBatch?.length}
                    className="px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-colors flex items-center gap-2"
                  >
                    {teamAuctionPreviewLoading ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Anteprima...
                      </>
                    ) : (
                      <>Anteprima</>
                    )}
                  </button>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Team selezionati</label>
                  <div className="flex flex-wrap gap-2 min-h-[44px] p-3 bg-gray-700/50 border border-gray-600 rounded-lg">
                    {selectedTeamsForBatch.length === 0 ? (
                      <span className="text-gray-500 text-sm">Nessun team selezionato. Scegli lega e team sopra.</span>
                    ) : (
                      selectedTeamsForBatch.map((t) => (
                        <span
                          key={t}
                          className="inline-flex items-center gap-1 px-3 py-1.5 bg-purple-600/30 border border-purple-500/50 rounded-full text-sm"
                        >
                          {t}
                          <button
                            type="button"
                            onClick={() => setSelectedTeamsForBatch(selectedTeamsForBatch.filter((x) => x !== t))}
                            className="p-0.5 rounded-full hover:bg-purple-500/50 text-gray-300 hover:text-white"
                            title="Rimuovi"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </span>
                      ))
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Company Cards Grid */}
            {companyCardsLoading && (
              <div className="text-center py-12">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-400"></div>
                <p className="mt-4 text-gray-400">Loading...</p>
              </div>
            )}
            {!companyCardsLoading && Array.isArray(companyCards) && companyCards.length > 0 ? (
              <>
                <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-6">
                  {(Array.isArray(companyCards) ? companyCards : []).map((card) => (
                  <div key={`template-${card.templateId}`} className="bg-gray-800 rounded-lg p-4 border border-gray-700 hover:border-blue-500 transition-all">
                  <div className="w-full h-48 bg-gradient-to-br from-gray-600 to-gray-800 rounded-lg flex items-center justify-center mb-4 relative overflow-hidden">
                    {/* Badge with available copies count */}
                    {card.availableCopies !== undefined && card.availableCopies > 0 && (
                      <div className="absolute top-2 left-2 z-10 bg-blue-600 text-white rounded-full w-10 h-10 flex items-center justify-center font-bold text-sm shadow-lg border-2 border-white/50">
                        {card.availableCopies}
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={() => {
                        setInfoCard(card);
                        setDeleteCardQuantity(1);
                        setShowInfoModal(true);
                      }}
                      className="absolute top-2 right-2 z-10 p-2 rounded-full bg-gray-900/80 text-white hover:bg-gray-700 border border-gray-600"
                      title="Card Info"
                    >
                      <Info className="w-4 h-4" />
                    </button>
                    <CardImage
                      card={{
                        ...card,
                        player: {
                          ...(card.player || {}),
                          image_path: card.player?.image_path || card.player?.imagePath
                        }
                      }}
                      alt={getCardDisplayName(card)}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent flex items-end justify-center pb-2">
                      <div className="text-center text-white">
                        <div className="font-bold text-lg drop-shadow-lg">{getCardDisplayName(card)}</div>
                        {card.player?.position && (
                          <div className="text-xs mt-1 opacity-90 drop-shadow-lg">{card.player.position}</div>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div className="mb-3 flex items-center justify-between">
                    <div className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold border border-gray-600 bg-gray-700">
                      <span className="mr-1">⭐</span>
                      {card.rarity?.displayName || card.rarity?.name || 'Basic'}
                    </div>
                    {(card.isInSale || card.isInAuction || card.isOnLoan) && (
                      <div className="flex space-x-1">
                        {card.isInSale && (
                          <span className="px-2 py-1 rounded-full text-xs font-semibold bg-green-600/30 text-green-300 border border-green-500/50">
                            Sale
                          </span>
                        )}
                        {card.isInAuction && (
                          <span className="px-2 py-1 rounded-full text-xs font-semibold bg-purple-600/30 text-purple-300 border border-purple-500/50">
                            Auction
                          </span>
                        )}
                        {card.isOnLoan && (
                          <span className="px-2 py-1 rounded-full text-xs font-semibold bg-yellow-600/30 text-yellow-300 border border-yellow-500/50">
                            Loan
                          </span>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="grid grid-cols-3 gap-2">
                    <button
                      onClick={() => {
                        setSelectedCard(card);
                        setShowSaleModal(true);
                      }}
                      disabled={card.isInSale || card.isInAuction || card.isOnLoan}
                      className={`px-3 py-2 text-white text-sm font-semibold rounded transition-all flex items-center justify-center space-x-1 ${
                        card.isInSale || card.isInAuction || card.isOnLoan
                          ? 'bg-gray-600 cursor-not-allowed opacity-50'
                          : 'bg-green-600 hover:bg-green-700'
                      }`}
                    >
                      <DollarSign className="w-4 h-4" />
                      <span>Sell</span>
                    </button>
                    <button
                      onClick={() => {
                        setSelectedCard(card);
                        const rarityName = card.rarity?.name || card.rarity?.displayName || card.rarity?.display_name;
                        setAuctionPriceMode('fixed');
                        setAuctionStartingPrice(getDefaultAuctionPrice(rarityName).toString());
                        setShowAuctionModal(true);
                      }}
                      disabled={card.isInSale || card.isInAuction || card.isOnLoan}
                      className={`px-3 py-2 text-white text-sm font-semibold rounded transition-all flex items-center justify-center space-x-1 ${
                        card.isInSale || card.isInAuction || card.isOnLoan
                          ? 'bg-gray-600 cursor-not-allowed opacity-50'
                          : 'bg-purple-600 hover:bg-purple-700'
                      }`}
                    >
                      <Gavel className="w-4 h-4" />
                      <span>Auction</span>
                    </button>
                    <button
                      onClick={() => {
                        setSelectedCard(card);
                        setShowLoanModal(true);
                      }}
                      disabled={card.isInSale || card.isInAuction || card.isOnLoan}
                      className={`px-3 py-2 text-white text-sm font-semibold rounded transition-all flex items-center justify-center space-x-1 ${
                        card.isInSale || card.isInAuction || card.isOnLoan
                          ? 'bg-gray-600 cursor-not-allowed opacity-50'
                          : 'bg-yellow-600 hover:bg-yellow-700'
                      }`}
                    >
                      <Clock className="w-4 h-4" />
                      <span>Loan</span>
                    </button>
                  </div>
                </div>
                  ))}
                </div>
                {allCompanyCards.length > COMPANY_CARDS_PER_PAGE && (
                  <div className="flex items-center justify-center flex-wrap gap-2 mt-6">
                    <button
                      onClick={() => setPageCompanyCards(0)}
                      disabled={pageCompanyCards === 0}
                      className={`px-3 py-2 rounded-lg font-semibold transition-all flex items-center ${
                        pageCompanyCards === 0
                          ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                          : 'bg-blue-600 text-white hover:bg-blue-700'
                      }`}
                      title="First page"
                    >
                      <ChevronsLeft className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => setPageCompanyCards(Math.max(0, pageCompanyCards - 1))}
                      disabled={pageCompanyCards === 0}
                      className={`px-3 py-2 rounded-lg font-semibold transition-all flex items-center ${
                        pageCompanyCards === 0
                          ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                          : 'bg-blue-600 text-white hover:bg-blue-700'
                      }`}
                    >
                      <ChevronLeft className="w-5 h-5 mr-1" />
                      Previous
                    </button>
                    <span className="text-white/90 text-sm font-medium px-2">
                      Page {pageCompanyCards + 1} of {Math.ceil(allCompanyCards.length / COMPANY_CARDS_PER_PAGE)}
                    </span>
                    <button
                      onClick={() => setPageCompanyCards(Math.min(Math.ceil(allCompanyCards.length / COMPANY_CARDS_PER_PAGE) - 1, pageCompanyCards + 1))}
                      disabled={pageCompanyCards >= Math.ceil(allCompanyCards.length / COMPANY_CARDS_PER_PAGE) - 1}
                      className={`px-3 py-2 rounded-lg font-semibold transition-all flex items-center ${
                        pageCompanyCards >= Math.ceil(allCompanyCards.length / COMPANY_CARDS_PER_PAGE) - 1
                          ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                          : 'bg-blue-600 text-white hover:bg-blue-700'
                      }`}
                    >
                      Next
                      <ChevronRight className="w-5 h-5 ml-1" />
                    </button>
                    <button
                      onClick={() => setPageCompanyCards(Math.ceil(allCompanyCards.length / COMPANY_CARDS_PER_PAGE) - 1)}
                      disabled={pageCompanyCards >= Math.ceil(allCompanyCards.length / COMPANY_CARDS_PER_PAGE) - 1}
                      className={`px-3 py-2 rounded-lg font-semibold transition-all flex items-center ${
                        pageCompanyCards >= Math.ceil(allCompanyCards.length / COMPANY_CARDS_PER_PAGE) - 1
                          ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                          : 'bg-blue-600 text-white hover:bg-blue-700'
                      }`}
                      title="Last page"
                    >
                      <ChevronsRight className="w-5 h-5" />
                    </button>
                  </div>
                )}
              </>
            ) : (
              !loading && (
                <div className="text-center py-12 text-gray-400">
                  <Package className="w-16 h-16 mx-auto mb-4 opacity-50" />
                  <p>No company cards available</p>
                </div>
              )
            )}
          </>
        )}

        {!loading && activeTab === 'concluded-auctions' && (
          <div>
            <h2 className="text-2xl font-bold mb-6">Storico aste concluse</h2>
            <div className="mb-6">
              <div className="relative max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Cerca per nome giocatore o username vincitore..."
                  value={concludedSearch}
                  onChange={(e) => setConcludedSearch(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
                />
              </div>
            </div>
            {(Array.isArray(concludedAuctions) ? concludedAuctions : []).length > 0 ? (
              <>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {(Array.isArray(concludedAuctions) ? concludedAuctions : []).map((auction) => (
                    <div
                      key={auction.id}
                      className={`bg-gray-800 rounded-lg p-6 border ${
                        auction.status === 'cancelled' ? 'border-amber-500/50' : 'border-gray-700'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-4">
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                          auction.status === 'ended'
                            ? 'bg-green-600/30 text-green-300 border border-green-500/50'
                            : 'bg-amber-600/30 text-amber-300 border border-amber-500/50'
                        }`}>
                          {auction.status === 'ended' ? 'Conclusa' : 'Annullata'}
                        </span>
                        <span className="text-gray-400 text-xs">
                          {auction.end_time ? new Date(auction.end_time).toLocaleString() : ''}
                        </span>
                      </div>
                      <h3 className="text-xl font-bold text-white mb-3">
                        {auction.player?.display_name || auction.player?.full_name || `Asta #${auction.id}`}
                      </h3>
                      <div className="space-y-2 mb-4">
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-gray-400">Card ID:</span>
                          <span className="text-gray-300 font-mono text-xs">{auction.card_id ?? auction.id}</span>
                        </div>
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-gray-400">Rarità:</span>
                          <span className="text-white font-semibold">
                            ⭐ {auction.rarity?.display_name || auction.rarity?.name || 'N/A'}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-gray-400 text-sm">Prezzo finale:</span>
                          <span className="text-purple-400 font-bold">
                            {parseFloat(auction.current_price || 0).toFixed(2)} DIOS
                          </span>
                        </div>
                        <div className="flex items-center justify-between mt-1">
                          <span className="text-gray-400 text-xs">Vincitore:</span>
                          {auction.current_bidder ? (
                            <span className="text-green-400 font-semibold text-xs">
                              👑 {auction.current_bidder.username}
                            </span>
                          ) : (
                            <span className="text-amber-400/90 font-medium text-xs italic">
                              Nessun vincitore
                            </span>
                          )}
                        </div>
                        {(auction.seller || auction.seller_id === null) && (
                          <div className="flex items-center justify-between mt-1">
                            <span className="text-gray-400 text-xs">Venditore:</span>
                            <span className="text-gray-300 text-xs">
                              {auction.seller_id == null ? '🏢 Company' : (auction.seller?.username || '-')}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                {totalConcludedAuctions > ITEMS_PER_PAGE && (
                  <div className="flex items-center justify-center space-x-4 mt-6">
                    <button
                      onClick={() => setPageConcludedAuctions(Math.max(0, pageConcludedAuctions - 1))}
                      disabled={pageConcludedAuctions === 0}
                      className={`px-4 py-2 rounded-lg font-semibold transition-all flex items-center ${
                        pageConcludedAuctions === 0
                          ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                          : 'bg-blue-600 text-white hover:bg-blue-700'
                      }`}
                    >
                      <ChevronLeft className="w-5 h-5 mr-1" />
                      Precedente
                    </button>
                    <span className="text-white/90 text-sm font-medium">
                      Pagina {pageConcludedAuctions + 1} di {Math.ceil(totalConcludedAuctions / ITEMS_PER_PAGE)} ({totalConcludedAuctions} totali)
                    </span>
                    <button
                      onClick={() => setPageConcludedAuctions(Math.min(Math.ceil(totalConcludedAuctions / ITEMS_PER_PAGE) - 1, pageConcludedAuctions + 1))}
                      disabled={pageConcludedAuctions >= Math.ceil(totalConcludedAuctions / ITEMS_PER_PAGE) - 1}
                      className={`px-4 py-2 rounded-lg font-semibold transition-all flex items-center ${
                        pageConcludedAuctions >= Math.ceil(totalConcludedAuctions / ITEMS_PER_PAGE) - 1
                          ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                          : 'bg-blue-600 text-white hover:bg-blue-700'
                      }`}
                    >
                      Successiva
                      <ChevronRight className="w-5 h-5 ml-1" />
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-12 text-gray-400">
                <History className="w-16 h-16 mx-auto mb-4 opacity-50" />
                <p>{debouncedConcludedSearch.trim() ? 'Nessun risultato per questa ricerca' : 'Nessuna asta conclusa'}</p>
              </div>
            )}
          </div>
        )}

        {!loading && activeTab === 'concluded-sales' && (
          <div>
            <h2 className="text-2xl font-bold mb-6">Storico sales concluse</h2>
            <div className="mb-6">
              <div className="relative max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Cerca per nome giocatore, buyer o seller..."
                  value={concludedSalesSearch}
                  onChange={(e) => setConcludedSalesSearch(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
                />
              </div>
            </div>
            {(Array.isArray(concludedSales) ? concludedSales : []).length > 0 ? (
              <>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {(Array.isArray(concludedSales) ? concludedSales : []).map((sale) => (
                    <div key={sale.id} className="bg-gray-800 rounded-lg p-6 border border-gray-700">
                      <div className="flex items-center justify-between mb-4">
                        <span className="px-3 py-1 rounded-full text-xs font-semibold bg-green-600/30 text-green-300 border border-green-500/50">
                          Conclusa
                        </span>
                        <span className="text-gray-400 text-xs">
                          {sale.sold_at ? new Date(sale.sold_at).toLocaleString() : ''}
                        </span>
                      </div>
                      <h3 className="text-xl font-bold text-white mb-3">
                        {sale.player?.display_name || sale.player?.full_name || `Sale #${sale.id}`}
                      </h3>
                      <div className="space-y-2 mb-4">
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-gray-400">Card ID:</span>
                          <span className="text-gray-300 font-mono text-xs">{sale.card_id ?? sale.id}</span>
                        </div>
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-gray-400">Rarita:</span>
                          <span className="text-white font-semibold">
                            ⭐ {sale.rarity?.display_name || sale.rarity?.name || 'N/A'}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-gray-400 text-sm">Prezzo:</span>
                          <span className="text-purple-400 font-bold">
                            {parseFloat(sale.price || 0).toFixed(2)} DIOS
                          </span>
                        </div>
                        <div className="flex items-center justify-between mt-1">
                          <span className="text-gray-400 text-xs">Buyer:</span>
                          <span className="text-green-400 font-semibold text-xs">
                            {sale.buyer?.username || '-'}
                          </span>
                        </div>
                        <div className="flex items-center justify-between mt-1">
                          <span className="text-gray-400 text-xs">Seller:</span>
                          <span className="text-gray-300 text-xs">
                            {sale.seller_id == null ? '🏢 Company' : (sale.seller?.username || '-')}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                {totalConcludedSales > ITEMS_PER_PAGE && (
                  <div className="flex items-center justify-center space-x-4 mt-6">
                    <button
                      onClick={() => setPageConcludedSales(Math.max(0, pageConcludedSales - 1))}
                      disabled={pageConcludedSales === 0}
                      className={`px-4 py-2 rounded-lg font-semibold transition-all flex items-center ${
                        pageConcludedSales === 0
                          ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                          : 'bg-blue-600 text-white hover:bg-blue-700'
                      }`}
                    >
                      <ChevronLeft className="w-5 h-5 mr-1" />
                      Precedente
                    </button>
                    <span className="text-white/90 text-sm font-medium">
                      Pagina {pageConcludedSales + 1} di {Math.ceil(totalConcludedSales / ITEMS_PER_PAGE)} ({totalConcludedSales} totali)
                    </span>
                    <button
                      onClick={() => setPageConcludedSales(Math.min(Math.ceil(totalConcludedSales / ITEMS_PER_PAGE) - 1, pageConcludedSales + 1))}
                      disabled={pageConcludedSales >= Math.ceil(totalConcludedSales / ITEMS_PER_PAGE) - 1}
                      className={`px-4 py-2 rounded-lg font-semibold transition-all flex items-center ${
                        pageConcludedSales >= Math.ceil(totalConcludedSales / ITEMS_PER_PAGE) - 1
                          ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                          : 'bg-blue-600 text-white hover:bg-blue-700'
                      }`}
                    >
                      Successiva
                      <ChevronRight className="w-5 h-5 ml-1" />
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-12 text-gray-400">
                <DollarSign className="w-16 h-16 mx-auto mb-4 opacity-50" />
                <p>{debouncedConcludedSalesSearch.trim() ? 'Nessun risultato per questa ricerca' : 'Nessuna sale conclusa'}</p>
              </div>
            )}
          </div>
        )}

        {!loading && activeTab === 'auctions' && (
          <div>
            <h2 className="text-2xl font-bold mb-6">All Active Auctions</h2>
            {(Array.isArray(allAuctions) ? allAuctions : []).length > 0 ? (
              <>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {(Array.isArray(allAuctions) ? allAuctions : []).slice(pageAllAuctions * ITEMS_PER_PAGE, (pageAllAuctions + 1) * ITEMS_PER_PAGE).map((auction) => (
                    <AdminAuctionCard key={auction.id} auction={auction} onCancel={handleCancelAuction} cancellingAuctionId={cancellingAuctionId} />
                  ))}
                </div>
                {(Array.isArray(allAuctions) ? allAuctions : []).length > ITEMS_PER_PAGE && (
                  <div className="flex items-center justify-center space-x-4 mt-6">
                    <button
                      onClick={() => setPageAllAuctions(Math.max(0, pageAllAuctions - 1))}
                      disabled={pageAllAuctions === 0}
                      className={`px-4 py-2 rounded-lg font-semibold transition-all flex items-center ${
                        pageAllAuctions === 0
                          ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                          : 'bg-blue-600 text-white hover:bg-blue-700'
                      }`}
                    >
                      <ChevronLeft className="w-5 h-5 mr-1" />
                      Previous
                    </button>
                    <span className="text-white/90 text-sm font-medium">
                      Page {pageAllAuctions + 1} of {Math.ceil((Array.isArray(allAuctions) ? allAuctions : []).length / ITEMS_PER_PAGE)}
                    </span>
                    <button
                      onClick={() => setPageAllAuctions(Math.min(Math.ceil((Array.isArray(allAuctions) ? allAuctions : []).length / ITEMS_PER_PAGE) - 1, pageAllAuctions + 1))}
                      disabled={pageAllAuctions >= Math.ceil((Array.isArray(allAuctions) ? allAuctions : []).length / ITEMS_PER_PAGE) - 1}
                      className={`px-4 py-2 rounded-lg font-semibold transition-all flex items-center ${
                        pageAllAuctions >= Math.ceil((Array.isArray(allAuctions) ? allAuctions : []).length / ITEMS_PER_PAGE) - 1
                          ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                          : 'bg-blue-600 text-white hover:bg-blue-700'
                      }`}
                    >
                      Next
                      <ChevronRight className="w-5 h-5 ml-1" />
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-12 text-gray-400">
                <Gavel className="w-16 h-16 mx-auto mb-4 opacity-50" />
                <p>No active auctions</p>
              </div>
            )}
          </div>
        )}

        {!loading && activeTab === 'sales' && (
          <div>
            <h2 className="text-2xl font-bold mb-6">All Active Sales</h2>
            {(Array.isArray(allSales) ? allSales : []).length > 0 ? (
              <>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {(Array.isArray(allSales) ? allSales : []).slice(pageAllSales * ITEMS_PER_PAGE, (pageAllSales + 1) * ITEMS_PER_PAGE).map((sale) => (
                    <AdminSaleCard key={sale.id} sale={sale} />
                  ))}
                </div>
                {(Array.isArray(allSales) ? allSales : []).length > ITEMS_PER_PAGE && (
                  <div className="flex items-center justify-center space-x-4 mt-6">
                    <button
                      onClick={() => setPageAllSales(Math.max(0, pageAllSales - 1))}
                      disabled={pageAllSales === 0}
                      className={`px-4 py-2 rounded-lg font-semibold transition-all flex items-center ${
                        pageAllSales === 0
                          ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                          : 'bg-blue-600 text-white hover:bg-blue-700'
                      }`}
                    >
                      <ChevronLeft className="w-5 h-5 mr-1" />
                      Previous
                    </button>
                    <span className="text-white/90 text-sm font-medium">
                      Page {pageAllSales + 1} of {Math.ceil((Array.isArray(allSales) ? allSales : []).length / ITEMS_PER_PAGE)}
                    </span>
                    <button
                      onClick={() => setPageAllSales(Math.min(Math.ceil((Array.isArray(allSales) ? allSales : []).length / ITEMS_PER_PAGE) - 1, pageAllSales + 1))}
                      disabled={pageAllSales >= Math.ceil((Array.isArray(allSales) ? allSales : []).length / ITEMS_PER_PAGE) - 1}
                      className={`px-4 py-2 rounded-lg font-semibold transition-all flex items-center ${
                        pageAllSales >= Math.ceil((Array.isArray(allSales) ? allSales : []).length / ITEMS_PER_PAGE) - 1
                          ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                          : 'bg-blue-600 text-white hover:bg-blue-700'
                      }`}
                    >
                      Next
                      <ChevronRight className="w-5 h-5 ml-1" />
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-12 text-gray-400">
                <DollarSign className="w-16 h-16 mx-auto mb-4 opacity-50" />
                <p>No active sales</p>
              </div>
            )}
          </div>
        )}

        {!loading && activeTab === 'loans' && (
          <div>
            <h2 className="text-2xl font-bold mb-6">All Active Loans</h2>
            {(Array.isArray(allLoans) ? allLoans : []).length > 0 ? (
              <>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {(Array.isArray(allLoans) ? allLoans : []).slice(pageAllLoans * ITEMS_PER_PAGE, (pageAllLoans + 1) * ITEMS_PER_PAGE).map((loan) => (
                    <AdminLoanCard key={loan.id} loan={loan} />
                  ))}
                </div>
                {(Array.isArray(allLoans) ? allLoans : []).length > ITEMS_PER_PAGE && (
                  <div className="flex items-center justify-center space-x-4 mt-6">
                    <button
                      onClick={() => setPageAllLoans(Math.max(0, pageAllLoans - 1))}
                      disabled={pageAllLoans === 0}
                      className={`px-4 py-2 rounded-lg font-semibold transition-all flex items-center ${
                        pageAllLoans === 0
                          ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                          : 'bg-blue-600 text-white hover:bg-blue-700'
                      }`}
                    >
                      <ChevronLeft className="w-5 h-5 mr-1" />
                      Previous
                    </button>
                    <span className="text-white/90 text-sm font-medium">
                      Page {pageAllLoans + 1} of {Math.ceil((Array.isArray(allLoans) ? allLoans : []).length / ITEMS_PER_PAGE)}
                    </span>
                    <button
                      onClick={() => setPageAllLoans(Math.min(Math.ceil((Array.isArray(allLoans) ? allLoans : []).length / ITEMS_PER_PAGE) - 1, pageAllLoans + 1))}
                      disabled={pageAllLoans >= Math.ceil((Array.isArray(allLoans) ? allLoans : []).length / ITEMS_PER_PAGE) - 1}
                      className={`px-4 py-2 rounded-lg font-semibold transition-all flex items-center ${
                        pageAllLoans >= Math.ceil((Array.isArray(allLoans) ? allLoans : []).length / ITEMS_PER_PAGE) - 1
                          ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                          : 'bg-blue-600 text-white hover:bg-blue-700'
                      }`}
                    >
                      Next
                      <ChevronRight className="w-5 h-5 ml-1" />
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-12 text-gray-400">
                <Clock className="w-16 h-16 mx-auto mb-4 opacity-50" />
                <p>No active loans</p>
              </div>
            )}
          </div>
        )}

        {!loading && activeTab === 'alerts' && (
          <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold flex items-center">
                <AlertTriangle className="w-6 h-6 mr-2 text-red-400" />
                Abuse Alerts
              </h2>
              <div className="flex items-center space-x-4">
                <select
                  value={alertFilters.status}
                  onChange={(e) => {
                    setAlertFilters({ ...alertFilters, status: e.target.value });
                    setTimeout(() => loadData(), 100);
                  }}
                  className="px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
                >
                  <option value="">All Status</option>
                  <option value="open">Open</option>
                  <option value="resolved">Resolved</option>
                  <option value="dismissed">Dismissed</option>
                </select>
                <select
                  value={alertFilters.severity}
                  onChange={(e) => {
                    setAlertFilters({ ...alertFilters, severity: e.target.value });
                    setTimeout(() => loadData(), 100);
                  }}
                  className="px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
                >
                  <option value="">All Severity</option>
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="critical">Critical</option>
                </select>
                <select
                  value={alertFilters.alertType}
                  onChange={(e) => {
                    setAlertFilters({ ...alertFilters, alertType: e.target.value });
                    setTimeout(() => loadData(), 100);
                  }}
                  className="px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
                >
                  <option value="">All Types</option>
                  <option value="multi_account">Multi Account</option>
                  <option value="suspicious_price">Suspicious Price</option>
                  <option value="suspicious_activity">Suspicious Activity</option>
                  <option value="fraud">Fraud</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </div>
            
            {abuseAlerts.length === 0 ? (
              <div className="text-center py-12">
                <AlertTriangle className="w-16 h-16 mx-auto mb-4 opacity-50 text-gray-600" />
                <p className="text-gray-400">No abuse alerts found</p>
              </div>
            ) : (
              <div className="space-y-4">
                {(Array.isArray(abuseAlerts) ? abuseAlerts : []).map((alert) => (
                  <div
                    key={alert.id}
                    className={`p-4 rounded-lg border ${
                      alert.severity === 'critical'
                        ? 'bg-red-900/20 border-red-500/50'
                        : alert.severity === 'high'
                        ? 'bg-orange-900/20 border-orange-500/50'
                        : alert.severity === 'medium'
                        ? 'bg-yellow-900/20 border-yellow-500/50'
                        : 'bg-gray-700/50 border-gray-600'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <span className={`px-2 py-1 rounded text-xs font-semibold ${
                            alert.severity === 'critical'
                              ? 'bg-red-600 text-white'
                              : alert.severity === 'high'
                              ? 'bg-orange-600 text-white'
                              : alert.severity === 'medium'
                              ? 'bg-yellow-600 text-white'
                              : 'bg-gray-600 text-white'
                          }`}>
                            {alert.severity?.toUpperCase() || 'UNKNOWN'}
                          </span>
                          <span className="px-2 py-1 rounded text-xs font-semibold bg-blue-600/30 text-blue-300 border border-blue-500/50">
                            {alert.alertType?.replace('_', ' ').toUpperCase() || 'UNKNOWN'}
                          </span>
                          <span className={`px-2 py-1 rounded text-xs font-semibold ${
                            alert.status === 'open'
                              ? 'bg-green-600/30 text-green-300 border border-green-500/50'
                              : alert.status === 'resolved'
                              ? 'bg-gray-600/30 text-gray-300 border border-gray-500/50'
                              : 'bg-gray-600/30 text-gray-300 border border-gray-500/50'
                          }`}>
                            {alert.status?.toUpperCase() || 'UNKNOWN'}
                          </span>
                        </div>
                        <p className="text-white font-semibold mb-1">
                          {alert.entityType?.toUpperCase()} #{alert.entityId}
                        </p>
                        {alert.user && (
                          <p className="text-gray-400 text-sm mb-2">
                            User: {alert.user.username} ({alert.user.email})
                          </p>
                        )}
                        <p className="text-gray-300 text-sm">{alert.description}</p>
                        <p className="text-gray-500 text-xs mt-2">
                          Created: {new Date(alert.createdAt || alert.created_at).toLocaleString()}
                        </p>
                      </div>
                      <div className="flex flex-col space-y-2 ml-4">
                        {alert.status === 'open' && (
                          <>
                            <button
                              onClick={async () => {
                                const resolution = window.prompt('Enter resolution notes:');
                                if (!resolution) return;
                                setLoading(true);
                                try {
                                  await adminApi.resolveAbuseAlert(alert.id, { resolution });
                                  setMessage({ type: 'success', text: '✅ Alert resolved!' });
                                  loadData();
                                } catch (error) {
                                  setMessage({ type: 'error', text: `❌ ${error.message}` });
                                } finally {
                                  setLoading(false);
                                }
                              }}
                              className="px-3 py-1 bg-green-600 hover:bg-green-700 text-white text-sm rounded transition-colors"
                            >
                              Resolve
                            </button>
                            <button
                              onClick={async () => {
                                setLoading(true);
                                try {
                                  await adminApi.resolveAbuseAlert(alert.id, { resolution: 'Dismissed by admin' });
                                  setMessage({ type: 'success', text: '✅ Alert dismissed!' });
                                  loadData();
                                } catch (error) {
                                  setMessage({ type: 'error', text: `❌ ${error.message}` });
                                } finally {
                                  setLoading(false);
                                }
                              }}
                              className="px-3 py-1 bg-gray-600 hover:bg-gray-700 text-white text-sm rounded transition-colors"
                            >
                              Dismiss
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Sale Modal */}
        {showSaleModal && selectedCard && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
            <div className="bg-gray-800 rounded-lg p-6 max-w-md w-full border border-gray-700">
              <h2 className="text-2xl font-bold mb-4">Sell Company Card</h2>
              <div className="mb-4">
                <p className="text-gray-300 mb-2">{getCardDisplayName(selectedCard)}</p>
                <p className="text-sm text-gray-400 mb-4">Available copies: {selectedCard.availableCopies || 0}</p>
                <label className="block text-gray-300 mb-2">Price (DIOS)</label>
                <input
                  type="number"
                  value={salePrice}
                  onChange={(e) => setSalePrice(e.target.value)}
                  placeholder="Enter price"
                  className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-blue-500 mb-4"
                  min="0.01"
                  step="0.01"
                />
                <label className="block text-gray-300 mb-2">Quantity</label>
                <input
                  type="number"
                  value={saleQuantity}
                  onChange={(e) => setSaleQuantity(e.target.value)}
                  placeholder="Enter quantity"
                  className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
                  min="1"
                  max={selectedCard.availableCopies || 1}
                  step="1"
                />
                <p className="text-xs text-gray-400 mt-2">10% fee will be deducted from sale</p>
              </div>
              <div className="flex space-x-4">
                <button
                  onClick={handleCreateCompanySale}
                  disabled={loading}
                  className="flex-1 px-6 py-3 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-lg transition-all"
                >
                  {loading ? 'Creating...' : 'List for Sale'}
                </button>
                <button
                  onClick={() => {
                    setShowSaleModal(false);
                    setSalePrice('');
                    setSaleQuantity(1);
                    setSelectedCard(null);
                  }}
                  className="px-6 py-3 border-2 border-gray-500 text-gray-300 hover:bg-gray-700 font-semibold rounded-lg transition-all"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Auction Modal */}
        {showAuctionModal && selectedCard && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
            <div className="bg-gray-800 rounded-lg p-6 max-w-md w-full border border-gray-700">
              <h2 className="text-2xl font-bold mb-4">Create Company Auction</h2>
              <div className="mb-4">
                <p className="text-gray-300 mb-2">{getCardDisplayName(selectedCard)}</p>
                <p className="text-sm text-gray-400 mb-4">Available copies: {selectedCard.availableCopies || 0}</p>
                <label className="block text-gray-300 mb-2">Starting Price (DIOS)</label>
                <div className="flex items-center space-x-2 mb-3">
                  <button
                    type="button"
                    onClick={() => {
                      const rarityName = selectedCard.rarity?.name || selectedCard.rarity?.displayName || selectedCard.rarity?.display_name;
                      setAuctionPriceMode('fixed');
                      setAuctionStartingPrice(getDefaultAuctionPrice(rarityName).toString());
                    }}
                    className={`px-3 py-1 rounded-lg text-sm font-semibold transition-all ${
                      auctionPriceMode === 'fixed'
                        ? 'bg-purple-600 text-white'
                        : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    }`}
                  >
                    Fixed Price
                  </button>
                  <button
                    type="button"
                    onClick={() => setAuctionPriceMode('custom')}
                    className={`px-3 py-1 rounded-lg text-sm font-semibold transition-all ${
                      auctionPriceMode === 'custom'
                        ? 'bg-purple-600 text-white'
                        : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    }`}
                  >
                    Custom Price
                  </button>
                </div>
                <input
                  type="number"
                  value={auctionStartingPrice}
                  onChange={(e) => setAuctionStartingPrice(e.target.value)}
                  placeholder={auctionPriceMode === 'custom' ? 'Enter starting price' : ''}
                  className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-blue-500 mb-4"
                  min="0.01"
                  step="0.01"
                  disabled={auctionPriceMode === 'fixed'}
                />
                <div className="grid grid-cols-3 gap-4 mb-4">
                  <div>
                    <label className="block text-gray-300 mb-2">Duration (Hours)</label>
                    <select
                      value={auctionDurationHours}
                      onChange={(e) => setAuctionDurationHours(parseInt(e.target.value))}
                      className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
                    >
                      <option value={0}>0 Hours</option>
                      <option value={12}>12 Hours</option>
                      <option value={24}>24 Hours</option>
                      <option value={48}>48 Hours</option>
                      <option value={72}>72 Hours</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-gray-300 mb-2">Minutes</label>
                    <input
                      type="number"
                      value={auctionDurationMinutes}
                      onChange={(e) => setAuctionDurationMinutes(parseInt(e.target.value, 10) || 0)}
                      min="0"
                      max="59"
                      className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-300 mb-2">Seconds</label>
                    <input
                      type="number"
                      value={auctionDurationSeconds}
                      onChange={(e) => setAuctionDurationSeconds(parseInt(e.target.value, 10) || 0)}
                      min="0"
                      max="59"
                      className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
                    />
                  </div>
                </div>
                <label className="block text-gray-300 mb-2">Quantity</label>
                <input
                  type="number"
                  value={auctionQuantity}
                  onChange={(e) => setAuctionQuantity(e.target.value)}
                  placeholder="Enter quantity"
                  className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
                  min="1"
                  max={selectedCard.availableCopies || 1}
                  step="1"
                />
                <p className="text-xs text-gray-400 mt-2">Minimum bid increment: 5% | +30 seconds per bid</p>
              </div>
              <div className="flex space-x-4">
                <button
                  onClick={handleCreateCompanyAuction}
                  disabled={loading}
                  className="flex-1 px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white font-semibold rounded-lg transition-all"
                >
                  {loading ? 'Creating...' : 'Create Auction'}
                </button>
                <button
                  onClick={() => {
                    setShowAuctionModal(false);
                    setAuctionStartingPrice('');
                    setAuctionPriceMode('fixed');
                    setAuctionDurationHours(24);
                    setAuctionDurationMinutes(0);
                    setAuctionDurationSeconds(0);
                    setAuctionQuantity(1);
                    setSelectedCard(null);
                  }}
                  className="px-6 py-3 border-2 border-gray-500 text-gray-300 hover:bg-gray-700 font-semibold rounded-lg transition-all"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Team Auction Preview Modal */}
        {showTeamAuctionPreviewModal && teamAuctionPreviewData && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
            <div className="bg-gray-800 rounded-lg p-6 max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col border border-gray-700">
              <h2 className="text-2xl font-bold mb-4">Anteprima asta team</h2>
              <div className="flex flex-wrap gap-4 mb-4 text-sm">
                <span className="px-3 py-1 bg-purple-600/30 rounded-full">{teamAuctionPreviewData.totalCards} card</span>
                <span className="px-3 py-1 bg-blue-600/30 rounded-full">{teamAuctionPreviewData.morningCount} fascia 00-08 (ogni ora)</span>
                <span className="px-3 py-1 bg-green-600/30 rounded-full">{teamAuctionPreviewData.afternoonCount} fascia 08-24 (ogni 15 min)</span>
                <span className="px-3 py-1 bg-gray-600/30 rounded-full">{teamAuctionPreviewData.daysNeeded} giorno/i</span>
              </div>
              <div className="flex-1 overflow-auto mb-4">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-gray-800">
                    <tr className="text-left text-gray-400 border-b border-gray-600">
                      <th className="py-2 px-2">#</th>
                      <th className="py-2 px-2">Team</th>
                      <th className="py-2 px-2">Giocatore</th>
                      <th className="py-2 px-2">Rarità</th>
                      <th className="py-2 px-2">Scadenza</th>
                      <th className="py-2 px-2">Prezzo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(teamAuctionPreviewData.slots || []).map((s) => {
                      const d = new Date(s.endTime);
                      const timeStr = d.toLocaleTimeString(locale || 'it-IT', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Rome' });
                      return (
                        <tr key={s.slotIndex} className="border-b border-gray-700 hover:bg-gray-700/50">
                          <td className="py-2 px-2">{s.slotIndex + 1}</td>
                          <td className="py-2 px-2">{s.team}</td>
                          <td className="py-2 px-2">{s.playerName}</td>
                          <td className="py-2 px-2">{s.rarityDisplayName}</td>
                          <td className="py-2 px-2">{timeStr}</td>
                          <td className="py-2 px-2">{s.defaultPrice} D10S</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="flex justify-end gap-4 pt-4 border-t border-gray-700">
                <button
                  onClick={() => {
                    setShowTeamAuctionPreviewModal(false);
                    setTeamAuctionPreviewData(null);
                  }}
                  className="px-6 py-3 border-2 border-gray-500 text-gray-300 hover:bg-gray-700 font-semibold rounded-lg transition-all"
                >
                  Annulla
                </button>
                <button
                  onClick={handleTeamAuctionConfirm}
                  disabled={teamAuctionConfirming}
                  className="px-6 py-3 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white font-semibold rounded-lg transition-all"
                >
                  {teamAuctionConfirming ? 'Creazione...' : 'Conferma e crea aste'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Loan Modal */}
        {showLoanModal && selectedCard && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
            <div className="bg-gray-800 rounded-lg p-6 max-w-md w-full border border-gray-700">
              <h2 className="text-2xl font-bold mb-4">Loan Company Card</h2>
              <div className="mb-4">
                <p className="text-gray-300 mb-2">{getCardDisplayName(selectedCard)}</p>
                <p className="text-sm text-gray-400 mb-4">Available copies: {selectedCard.availableCopies || 0}</p>
                <label className="block text-gray-300 mb-2">Price per Gameweek (DIOS)</label>
                <input
                  type="number"
                  value={loanPricePerGW}
                  onChange={(e) => setLoanPricePerGW(e.target.value)}
                  placeholder="Enter price per GW"
                  className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-blue-500 mb-4"
                  min="0.01"
                  step="0.01"
                />
                <label className="block text-gray-300 mb-2">Duration (Gameweeks)</label>
                <select
                  value={loanDurationGW}
                  onChange={(e) => setLoanDurationGW(parseInt(e.target.value))}
                  className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-blue-500 mb-4"
                >
                  <option value={1}>1 Gameweek</option>
                  <option value={3}>3 Gameweeks</option>
                  <option value={5}>5 Gameweeks</option>
                  <option value={10}>10 Gameweeks</option>
                </select>
                <label className="block text-gray-300 mb-2">Quantity</label>
                <input
                  type="number"
                  value={loanQuantity}
                  onChange={(e) => setLoanQuantity(e.target.value)}
                  placeholder="Enter quantity"
                  className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
                  min="1"
                  max={selectedCard.availableCopies || 1}
                  step="1"
                />
                {loanPricePerGW && loanDurationGW && (
                  <p className="text-xs text-gray-400 mt-2">
                    Total: {(parseFloat(loanPricePerGW) * loanDurationGW).toFixed(2)} DIOS (10% fee included)
                  </p>
                )}
              </div>
              <div className="flex space-x-4">
                <button
                  onClick={handleCreateCompanyLoan}
                  disabled={loading}
                  className="flex-1 px-6 py-3 bg-yellow-600 hover:bg-yellow-700 text-white font-semibold rounded-lg transition-all"
                >
                  {loading ? 'Creating...' : 'List for Loan'}
                </button>
                <button
                  onClick={() => {
                    setShowLoanModal(false);
                    setLoanPricePerGW('');
                    setLoanDurationGW(1);
                    setLoanQuantity(1);
                    setSelectedCard(null);
                  }}
                  className="px-6 py-3 border-2 border-gray-500 text-gray-300 hover:bg-gray-700 font-semibold rounded-lg transition-all"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Info Modal */}
        {showInfoModal && infoCard && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={() => setShowInfoModal(false)}>
            <div className="bg-gray-800 rounded-lg p-6 max-w-md w-full border border-gray-700" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-2xl font-bold flex items-center">
                  <Info className="w-5 h-5 mr-2 text-blue-400" />
                  Card Info
                </h2>
                <button
                  onClick={() => setShowInfoModal(false)}
                  className="text-gray-400 hover:text-white"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="space-y-3 text-gray-300">
                <div className="font-semibold text-white">{getCardDisplayName(infoCard)}</div>
                {getCardDisplayType(infoCard) && <div>Type: <span className="text-white">{getCardDisplayType(infoCard)}</span></div>}
                {infoCard.player?.team && <div>Team: <span className="text-white">{infoCard.player.team}</span></div>}
                {infoCard.player?.league && <div>League: <span className="text-white">{infoCard.player.league}</span></div>}
                {(infoCard.player?.is_out_of_dios10 || infoCard.player?.isOutOfDios10) && (
                  <div>
                    Status: <span className="inline-flex items-center px-2 py-0.5 rounded bg-red-900/40 border border-red-700 text-red-300 text-xs font-semibold">OUT OF DIOS</span>
                  </div>
                )}
                {infoCard.player?.position && <div>Position: <span className="text-white">{infoCard.player.position}</span></div>}
                {infoCard.player?.nationality && <div>Nationality: <span className="text-white">{infoCard.player.nationality}</span></div>}
                {(infoCard.rarity?.displayName || infoCard.rarity?.name) && <div>Rarity: <span className="text-white">{infoCard.rarity?.displayName || infoCard.rarity?.name}</span></div>}
                <div>Available copies: <span className="text-white">{infoCard.availableCopies ?? 0}</span></div>
                {['legend', 'divinity'].includes(String(infoCard.cardKind || infoCard.card_kind || '').toLowerCase()) && (infoCard.scoreBonusPercent != null || infoCard.scoreBonusPosition || infoCard.scoreBonusLeague) && (
                  <div className="pt-2 mt-2 border-t border-gray-600">
                    <div className="text-amber-300 font-medium text-sm">Bonus Legend/Divinity</div>
                    {infoCard.scoreBonusPosition && <div>Potenzia ruolo: <span className="text-white">{infoCard.scoreBonusPosition}</span></div>}
                    {infoCard.scoreBonusLeague && <div>Potenzia lega: <span className="text-white">{infoCard.scoreBonusLeague}</span></div>}
                    {infoCard.scoreBonusPercent != null && <div>Bonus score: <span className="text-white">+{Number(infoCard.scoreBonusPercent)}%</span></div>}
                  </div>
                )}
              </div>

              {/* Delete card (admin): remove company-owned copies from DB */}
              {(infoCard.availableCopies ?? 0) >= 1 && (
              <div className="mt-4 pt-4 border-t border-gray-600">
                <div className="flex items-center gap-2 mb-2">
                  <Trash2 className="w-4 h-4 text-red-400" />
                  <span className="font-semibold text-white">Delete card</span>
                </div>
                <p className="text-sm text-gray-400 mb-3">
                  Permanently remove company-owned copies of this card from the database.
                </p>
                {(infoCard.availableCopies ?? 0) > 1 ? (
                  <div className="flex flex-wrap items-center gap-2">
                    <label className="text-gray-300 text-sm">How many to delete?</label>
                    <input
                      type="number"
                      min={1}
                      max={infoCard.availableCopies ?? 1}
                      value={deleteCardQuantity}
                      onChange={(e) => setDeleteCardQuantity(Math.max(1, Math.min((infoCard.availableCopies ?? 1), parseInt(e.target.value, 10) || 1)))}
                      className="w-20 px-2 py-1.5 bg-gray-700 border border-gray-600 rounded text-white text-sm"
                    />
                    <span className="text-gray-400 text-sm">/ {infoCard.availableCopies ?? 0}</span>
                    <button
                      type="button"
                      onClick={async () => {
                        const qty = Math.min(deleteCardQuantity, infoCard.availableCopies ?? 0);
                        if (qty < 1) return;
                        if (!window.confirm(`Delete ${qty} copy/copies of this card? This cannot be undone.`)) return;
                        setDeletingCompanyCards(true);
                        try {
                          const res = await adminApi.deleteCompanyCards(infoCard.templateId, qty);
                          if (res?.success) {
                            setMessage({ type: 'success', text: `Deleted ${res.deleted ?? qty} card(s).` });
                            setShowInfoModal(false);
                            setInfoCard(null);
                            loadData();
                          } else {
                            setMessage({ type: 'error', text: res?.error || 'Delete failed' });
                          }
                        } catch (err) {
                          setMessage({ type: 'error', text: err?.data?.error || err?.message || 'Delete failed' });
                        } finally {
                          setDeletingCompanyCards(false);
                        }
                      }}
                      disabled={deletingCompanyCards}
                      className="px-3 py-1.5 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white text-sm font-medium rounded"
                    >
                      {deletingCompanyCards ? 'Deleting...' : `Delete ${Math.min(deleteCardQuantity, infoCard.availableCopies ?? 0)}`}
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={async () => {
                      if (!window.confirm('Delete this card permanently? This cannot be undone.')) return;
                      setDeletingCompanyCards(true);
                      try {
                        const res = await adminApi.deleteCompanyCards(infoCard.templateId, 1);
                        if (res?.success) {
                          setMessage({ type: 'success', text: 'Card deleted.' });
                          setShowInfoModal(false);
                          setInfoCard(null);
                          loadData();
                        } else {
                          setMessage({ type: 'error', text: res?.error || 'Delete failed' });
                        }
                      } catch (err) {
                        setMessage({ type: 'error', text: err?.data?.error || err?.message || 'Delete failed' });
                      } finally {
                        setDeletingCompanyCards(false);
                      }
                    }}
                    disabled={deletingCompanyCards || (infoCard.availableCopies ?? 0) < 1}
                    className="px-3 py-1.5 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white text-sm font-medium rounded flex items-center gap-1"
                  >
                    <Trash2 className="w-4 h-4" />
                    {deletingCompanyCards ? 'Deleting...' : 'Delete 1 card'}
                  </button>
                )}
              </div>
              )}
            </div>
          </div>
        )}

        {/* Edit Sale Price Modal */}
        {showEditSaleModal && editingSale && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
            <div className="bg-gray-800 rounded-lg p-6 max-w-md w-full border border-gray-700">
              <h2 className="text-2xl font-bold mb-4">Edit Sale Price</h2>
              <div className="mb-4">
                <p className="text-gray-300 mb-2">{editingSale.player?.display_name || editingSale.player?.full_name || `Card #${editingSale.card_id}`}</p>
                <label className="block text-gray-300 mb-2">New Price (DIOS)</label>
                <input
                  type="number"
                  value={newSalePrice}
                  onChange={(e) => setNewSalePrice(e.target.value)}
                  placeholder="Enter new price"
                  className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
                  min="0.01"
                  step="0.01"
                />
                <p className="text-xs text-gray-400 mt-2">Current price: {parseFloat(editingSale.price).toFixed(2)} DIOS</p>
              </div>
              <div className="flex space-x-4">
                <button
                  onClick={handleUpdateSalePrice}
                  disabled={loading}
                  className="flex-1 px-6 py-3 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-lg transition-all"
                >
                  {loading ? 'Updating...' : 'Update Price'}
                </button>
                <button
                  onClick={() => {
                    setShowEditSaleModal(false);
                    setEditingSale(null);
                    setNewSalePrice('');
                  }}
                  className="px-6 py-3 border-2 border-gray-500 text-gray-300 hover:bg-gray-700 font-semibold rounded-lg transition-all"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Edit Auction End Time Modal */}
        {showEditAuctionModal && editingAuction && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
            <div className="bg-gray-800 rounded-lg p-6 max-w-md w-full border border-gray-700">
              <h2 className="text-2xl font-bold mb-4">Edit Auction End Time</h2>
              <div className="mb-4">
                <p className="text-gray-300 mb-2">{editingAuction.player?.display_name || editingAuction.player?.full_name || `Card #${editingAuction.card_id}`}</p>
                <label className="block text-gray-300 mb-2">New End Time</label>
                <input
                  type="datetime-local"
                  value={newAuctionEndTime}
                  onChange={(e) => setNewAuctionEndTime(e.target.value)}
                  className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
                />
                <p className="text-xs text-gray-400 mt-2">
                  Current end time: {new Date(editingAuction.end_time).toLocaleString()}
                </p>
                {editingAuction.current_bidder && (
                  <p className="text-xs text-green-400 mt-2">
                    Current winner: {editingAuction.current_bidder.username}
                  </p>
                )}
              </div>
              <div className="flex space-x-4">
                <button
                  onClick={handleUpdateAuctionEndTime}
                  disabled={loading}
                  className="flex-1 px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white font-semibold rounded-lg transition-all"
                >
                  {loading ? 'Updating...' : 'Update End Time'}
                </button>
                <button
                  onClick={() => {
                    setShowEditAuctionModal(false);
                    setEditingAuction(null);
                    setNewAuctionEndTime('');
                  }}
                  className="px-6 py-3 border-2 border-gray-500 text-gray-300 hover:bg-gray-700 font-semibold rounded-lg transition-all"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Edit Loan Details Modal */}
        {showEditLoanModal && editingLoan && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
            <div className="bg-gray-800 rounded-lg p-6 max-w-md w-full border border-gray-700">
              <h2 className="text-2xl font-bold mb-4">Edit Loan Details</h2>
              <div className="mb-4">
                <p className="text-gray-300 mb-2">{editingLoan.player?.display_name || editingLoan.player?.full_name || `Card #${editingLoan.card_id}`}</p>
                <label className="block text-gray-300 mb-2">Price per Gameweek (DIOS)</label>
                <input
                  type="number"
                  value={newLoanPricePerGW}
                  onChange={(e) => setNewLoanPricePerGW(e.target.value)}
                  placeholder="Enter price per GW"
                  className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-blue-500 mb-4"
                  min="0.01"
                  step="0.01"
                />
                <label className="block text-gray-300 mb-2">Duration (Gameweeks)</label>
                <select
                  value={newLoanDurationGW}
                  onChange={(e) => setNewLoanDurationGW(parseInt(e.target.value))}
                  className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
                >
                  <option value={1}>1 Gameweek</option>
                  <option value={3}>3 Gameweeks</option>
                  <option value={5}>5 Gameweeks</option>
                  <option value={10}>10 Gameweeks</option>
                </select>
                {editingLoan.borrower && (
                  <p className="text-xs text-blue-400 mt-2">
                    Current borrower: {editingLoan.borrower.username}
                  </p>
                )}
                {newLoanPricePerGW && newLoanDurationGW && (
                  <p className="text-xs text-gray-400 mt-2">
                    Total: {(parseFloat(newLoanPricePerGW) * newLoanDurationGW).toFixed(2)} DIOS (10% fee included)
                  </p>
                )}
              </div>
              <div className="flex space-x-4">
                <button
                  onClick={handleUpdateLoanDetails}
                  disabled={loading}
                  className="flex-1 px-6 py-3 bg-yellow-600 hover:bg-yellow-700 text-white font-semibold rounded-lg transition-all"
                >
                  {loading ? 'Updating...' : 'Update Loan'}
                </button>
                <button
                  onClick={() => {
                    setShowEditLoanModal(false);
                    setEditingLoan(null);
                    setNewLoanPricePerGW('');
                    setNewLoanDurationGW(1);
                  }}
                  className="px-6 py-3 border-2 border-gray-500 text-gray-300 hover:bg-gray-700 font-semibold rounded-lg transition-all"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminMarketplace;
